import useSWR from "swr";
import { API } from "../utils/constants";
import { fetcher } from "./fetcher";

/**
 * useMenu — Fetches the full public menu (categories with nested items).
 *
 * API: GET /api/menu → MenuResponse (CON-002 §2)
 * Shape: { categories: [{ id, name, displayOrder, items: [MenuItemDto] }] }
 *
 * SWR config:
 * - dedupingInterval: 60s — menu rarely changes during a session
 * - revalidateOnFocus: false — avoid noisy refetches
 *
 * @returns {{ categories: Array, error: any, isLoading: boolean, mutate: Function }}
 */
export function useMenu() {
  const { data, error, isLoading, mutate } = useSWR(
    API.MENU.LIST,
    fetcher,
    { dedupingInterval: 60_000, revalidateOnFocus: false }
  );

  return {
    categories: data?.categories ?? [],
    error,
    isLoading,
    mutate,
  };
}

/**
 * useMenuItem — Fetches a single public menu item by ID.
 *
 * API: GET /api/menu/{id} → MenuItemDto (CON-002 §2)
 * Conditional fetch: passes null key when id is falsy (SWR skips the request).
 *
 * @param {number|string|null} id - Menu item ID from route params
 * @returns {{ item: object|null, error: any, isLoading: boolean }}
 */
export function useMenuItem(id) {
  const { data, error, isLoading } = useSWR(
    id ? API.MENU.DETAIL(id) : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  return { item: data ?? null, error, isLoading };
}
